import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { api, unwrap } from "@/lib/api";
import { useServiceDetail } from "@/lib/service-detail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ConfirmDialog } from "@/components/v2/ConfirmDialog";
import { useToast } from "@/components/v2/Toast";

/**
 * Service Detail · Settings tab — Pencil `ohSDe`. Edits the service group
 * basics (name, description), booking-flow tier picker, payment policy,
 * Visibility toggle + destructive Delete. Pricing/duration/capacity live on
 * the linked inventory items, not the service.
 */

const schema = z.object({
  name: z.string().min(2, "Name too short"),
  description: z.string().optional(),
  active: z.boolean(),
  tierPickerStyle: z.enum(["auto", "radio", "select"]),
  tierLabel: z.string().optional(),
  depositPolicy: z.enum(["NONE", "PERCENT", "AMOUNT"]),
  depositValue: z
    .union([z.string(), z.number()])
    .optional()
    .transform((v) => (v === "" || v == null ? undefined : Number(v)))
    .refine((v) => v === undefined || (Number.isFinite(v) && v >= 0), "Must be ≥ 0"),
});
type FormValues = z.infer<typeof schema>;

export function ServiceSettings() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const detail = useServiceDetail(id);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: "",
      description: "",
      active: true,
      tierPickerStyle: "auto",
      tierLabel: "",
      depositPolicy: "NONE",
      depositValue: undefined,
    },
  });

  // Prefill once the detail loads. Several columns (tierPickerStyle,
  // tierLabel, depositPolicy/Value) lag the OpenAPI codegen — read them
  // through casts the same way.
  useEffect(() => {
    const s = detail.data?.base;
    if (!s) return;
    const ext = s as {
      tierPickerStyle?: "auto" | "radio" | "select";
      tierLabel?: string | null;
      depositPolicy?: "NONE" | "PERCENT" | "AMOUNT";
      depositValue?: number | string | null;
    };
    reset({
      name: s.name,
      description: s.description ?? "",
      active: s.active,
      tierPickerStyle: ext.tierPickerStyle ?? "auto",
      tierLabel: ext.tierLabel ?? "",
      depositPolicy: ext.depositPolicy ?? "NONE",
      depositValue:
        ext.depositValue == null
          ? undefined
          : typeof ext.depositValue === "string"
            ? parseFloat(ext.depositValue)
            : ext.depositValue,
    });
  }, [detail.data, reset]);

  const update = useMutation({
    mutationFn: async (values: FormValues) =>
      unwrap(
        await api.PATCH("/api/v1/services/{id}", {
          params: { path: { id: id! } },
          // The OpenAPI codegen lags `tierPickerStyle`. Cast through `unknown`
          // so we can include it without losing the rest of the typed body.
          body: {
            name: values.name,
            description: values.description?.trim() || undefined,
            active: values.active,
            tierPickerStyle: values.tierPickerStyle,
            // Empty input clears the override on the backend (it stores null).
            tierLabel: values.tierLabel?.trim() ?? "",
            depositPolicy: values.depositPolicy,
            // Backend wipes depositValue automatically when policy=NONE.
            depositValue:
              values.depositPolicy === "NONE" ? undefined : values.depositValue,
          } as unknown as { name: string },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["service", id] });
      qc.invalidateQueries({ queryKey: ["services"] });
      toast.success("Service updated");
    },
    onError: (e) => toast.error("Couldn't save service", (e as Error).message),
  });

  const remove = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.DELETE("/api/v1/services/{id}", {
          params: { path: { id: id! } },
        }),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["services"] });
      toast.success("Service hidden", "Existing bookings keep their data.");
      navigate("/services");
    },
    onError: (e) => {
      toast.error("Couldn't delete service", (e as Error).message);
      setConfirmDelete(false);
    },
  });

  const active = watch("active");

  return (
    <form
      onSubmit={handleSubmit((v) => update.mutate(v))}
      className="space-y-6"
    >
      <Section
        title="Basics"
        description="Visible on the public booking page and the operator app."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-1.5 md:col-span-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" {...register("name")} />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>
          <div className="grid gap-1.5 md:col-span-2">
            <Label htmlFor="description">Description</Label>
            <textarea
              id="description"
              rows={3}
              {...register("description")}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>
      </Section>

      <Section
        title="Booking flow"
        description="How customers pick a tier in the New Booking panel for this service."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="tierPickerStyle">Tier picker style</Label>
            <Controller
              control={control}
              name="tierPickerStyle"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="tierPickerStyle" className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">
                      Auto — radio if ≤5 tiers, dropdown otherwise
                    </SelectItem>
                    <SelectItem value="radio">
                      Always show as radio cards
                    </SelectItem>
                    <SelectItem value="select">
                      Always show as dropdown
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            <p className="text-[11px] text-muted-foreground">
              Radio cards work great for a few tiers; switch to a dropdown
              when you have many tier options to avoid an overlong form.
            </p>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="tierLabel">Tier label (booking section title)</Label>
            <Input
              id="tierLabel"
              placeholder="Auto — uses linked category name"
              {...register("tierLabel")}
            />
            <p className="text-[11px] text-muted-foreground">
              Override what the New Booking flow calls this picker. Empty =
              auto-derive (uses the linked category name when all tiers
              share one, else "Plan"). Examples: "Gowns", "Package", "Service".
            </p>
          </div>
        </div>
      </Section>

      <Section
        title="Payment policy"
        description="What it takes for a booking to flip from Pending to Confirmed."
      >
        <div className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-1.5">
            <Label htmlFor="depositPolicy">Confirmation requires</Label>
            <Controller
              control={control}
              name="depositPolicy"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger id="depositPolicy" className="h-10">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">
                      Full payment of booking total
                    </SelectItem>
                    <SelectItem value="PERCENT">Percent deposit</SelectItem>
                    <SelectItem value="AMOUNT">Fixed-amount deposit</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            <p className="text-[11px] text-muted-foreground">
              Booking confirms once the sum of verified payments meets this
              threshold.
            </p>
          </div>
          {watch("depositPolicy") !== "NONE" && (
            <div className="grid gap-1.5">
              <Label htmlFor="depositValue">
                {watch("depositPolicy") === "PERCENT" ? "Percent" : "Amount"}
              </Label>
              <Input
                id="depositValue"
                type="number"
                step={watch("depositPolicy") === "PERCENT" ? "1" : "0.01"}
                min={0}
                {...register("depositValue")}
              />
              <p className="text-[11px] text-muted-foreground">
                {watch("depositPolicy") === "PERCENT"
                  ? "0–100. e.g. 50 = booking confirms once half the price is paid."
                  : `In ${detail.data?.base?.currency ?? "the booking currency"}. e.g. 1000 = booking confirms after 1,000 paid.`}
              </p>
              {errors.depositValue && (
                <p className="text-xs text-destructive">{errors.depositValue.message}</p>
              )}
            </div>
          )}
        </div>
      </Section>

      <Section
        title="Visibility"
        description="Hidden services don't appear on the public booking page; existing bookings stay intact."
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">{active ? "Active" : "Hidden"}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {active
                ? "Customers can find and book this service."
                : "Service is hidden from the public booking page."}
            </p>
          </div>
          <Switch
            checked={active}
            onCheckedChange={(v) => setValue("active", v, { shouldDirty: true })}
            aria-label={active ? "Mark service hidden" : "Mark service active"}
            className="h-6 w-11 [&>span]:h-5 [&>span]:w-5 [&>span]:data-[state=checked]:translate-x-5"
          />
        </div>
      </Section>

      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          className="text-destructive hover:bg-status-bg-cancelled"
          onClick={() => setConfirmDelete(true)}
        >
          Delete service
        </Button>
        <Button type="submit" disabled={!isDirty || isSubmitting || update.isPending}>
          {update.isPending ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => remove.mutate()}
        title={`Delete "${detail.data?.base?.name ?? "this service"}"?`}
        description="This is a soft-delete — the service is hidden from the public booking page and the catalogue. Existing bookings stay intact and continue to render."
        confirmLabel="Yes, delete service"
        pending={remove.isPending}
      />
    </form>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-lg border bg-card">
      <div className="px-5 py-4 border-b border-border">
        <p className="text-sm font-semibold tracking-tight">{title}</p>
        {description && (
          <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
        )}
      </div>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}
